import React, { useEffect, useState, useRef } from "react";
import Button from "components/button/Button";
import restClient from "restClients";
import LogoWhite from "../../assets/svg/railyard-logo-white.svg";
import LogoBlack from "../../assets/svg/railyard-logo-black.svg";
import { Link } from "react-router-dom";
import ChevronDownIcon from "assets/svg/chevron-down-icon";
import UserIcon from "assets/svg/user-icon";
import HamburgerIcon from "assets/svg/hamburger-icon";
import HamburgerMenu from "./HamburgerMenu";
import LocationsPopperMenu from "./LocationsPopperMenu";
import { useSelector } from "react-redux";
import HeartIcon from "assets/svg/heart-icon";
import LanguageMenu from "./LanguageMenu";
// import WhatsAppIcon from "assets/svg/whatsapp-icon";
// import PhoneSolidIcon from "assets/svg/phone-solid-icon";
import UKFlagIcon from "assets/svg/uk-flag-icon";
// import ChinaFlagIcon from "assets/svg/china-flag-icon";
import { trackEvents } from "utils/amplitude/amplitude";
import { clearLastVisitedSection } from "utils/scrollUtils";

const languageOptions = [
  {
    value: "en",
    label: "English",
    flag: UKFlagIcon,
  },
  // {
  //   value: "zh",
  //   label: "Chinese (普通话)",
  //   flag: ChinaFlagIcon,
  // },
];

export default function HeaderV2({
  headerContainerClass,
  isHeaderSticky,
  isCityPage,
  isPropertyPage,
}) {
  //  const navigate = useNavigate();
  const showLevel = useSelector((state) => state.level.showLevel);
  const headerRef = useRef(null);
  const languageMenuRef = useRef(null);
  const languageToggleRef = useRef(null);

  const [data, setData] = useState({});
  const [cityData, setCityData] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(languageOptions[0]);
  const [headerHeight, setHeaderHeight] = useState(0);

  // const citySlug = window.location?.pathname?.split("/")?.[1];
  // const propertySlug = window.location?.pathname?.split("/")?.[2];

  const getData = async () => {
    try {
      const { data } = await restClient.get("/header");
      if (data?.data) {
        setData(data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getCityData = async () => {
    try {
      const { data } = await restClient.get("/cities");
      setCityData(data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLocations = () => {
    setIsLocationsOpen(!isLocationsOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const handleLanguageSelect = (option) => {
    if (selectedLanguage.value !== option.value) {
      setSelectedLanguage(option);
      localStorage.setItem("locale", option.value);
      window.location.reload();
    }

    setIsLanguageMenuOpen(false);
  };

  useEffect(() => {
    if (isMenuOpen || isLocationsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [isMenuOpen, isLocationsOpen]);

  useEffect(() => {
    getData();
    getCityData();
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [data, isHeaderSticky]);

  const getInitialLanguage = () => {
    const storedLocale = localStorage.getItem("locale");
    if (storedLocale) {
      const matchingOption = languageOptions.find(
        (option) =>
          option.value === storedLocale ||
          option.value === storedLocale.split("-")[0]
      );
      if (matchingOption) return matchingOption;
      localStorage.removeItem("locale");
    }

    const browserLang = navigator.language || "en";
    const browserLangCode = browserLang.split("-")[0];
    const matchingOption = languageOptions.find(
      (option) => option.value === browserLangCode
    );

    if (matchingOption) {
      localStorage.setItem("locale", browserLangCode);
      return matchingOption;
    }

    return languageOptions[0];

  };

  useEffect(() => {
    const initialLanguage = getInitialLanguage();
    setSelectedLanguage(initialLanguage);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target) &&
        languageToggleRef.current &&
        !languageToggleRef.current.contains(event.target) && // Exclude toggle button clicks
        isLanguageMenuOpen
      ) {
        setIsLanguageMenuOpen(false);
      }
    };

    if (isLanguageMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageMenuOpen]);

  const FlagIcon = selectedLanguage?.flag;

  return (
    <>
      <div
        ref={headerRef}
        className={`${headerContainerClass} h-[55px] lg:h-[80px] px-[16px] 3xl:pl-[64px] 3xl:pr-[55px] shadow-header-shadow backdrop-blur-[30px] flex justify-between items-center`}
      >
        <div className="flex lg:gap-[58px]">
          <Link
            to="/"
            className="w-[83px] h-[24px] xxs:w-[93px] xxs:h-[24px] lg:w-[162px] lg:h-[42px] shrink-0"
            onClick={() => {
              clearLastVisitedSection("home");
            }}
          >
            <img
              src={isHeaderSticky ? LogoBlack : LogoWhite}
              alt="Company Logo"
              className="w-full h-full object-contain"
            />
          </Link>

          <div className="hidden lg:flex items-center shrink-0">
            <div>
              <button
                onClick={toggleLocations}
                className={`hover:text-secondary-rectangle-one flex items-center gap-[6px] font-bold text-[16px] ${
                  isHeaderSticky ? "text-primary-black" : "text-pure-white"
                }`}
              >
                <p>Locations</p>
                <ChevronDownIcon
                  className={`w-[10px] h-[10px] transition-transform duration-200 ${
                    isLocationsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-[4px] xxs:gap-[8px] 3xl:gap-[24px] items-center justify-end w-full">
          <Button
            onClick={() => {
              trackEvents("book_viewing_header_clicked");

              //navigate("/book-a-viewing/the-railyard");
              window.open(`/book-a-viewing/the-railyard`, "_blank");
            }}
            innerText={"Book a Viewing"}
            innerClass={`font-bold text-[14px] shrink-0 ${
              isHeaderSticky
                ? "text-primary-black lg:group-hover:text-pure-white"
                : "text-pure-white"
            }`}
            outerClass={`flex items-center justify-center group lg:h-[50px] lg:w-[160px] h-[36px] w-[130px] hidden md:inline-flex relative lg:hover:bg-secondary-rectangle-one sheen border ${
              isHeaderSticky
                ? "border-primary-black lg:hover:border-secondary-rectangle-one"
                : "border-pure-white lg:hover:border-secondary-rectangle-one"
            } rounded-[8px] px-[12px] py-[8px] lg:px-[18px] 2xl:px-[28px] 2xl:py-[12px] bg-transparent`}
          />
          <Button
            innerText={"Find Rooms"}
            innerClass={
              "text-pure-white lg:hover:text-primary-black font-bold text-[12px] md:text-[14px] shrink-0"
            }
            outerClass={`mr-9 xxs:mr-10 shadow-btn-shadow lg:shadow-none flex items-center justify-center border lg:h-[50px] lg:w-[160px] h-[30px] xxs:w-fit md:h-[36px] md:w-[130px] ${
              isHeaderSticky
                ? "border-secondary-rectangle-one lg:hover:border-primary-black"
                : "border-secondary-rectangle-one lg:hover:border-pure-white"
            } curved-hover rounded-[8px] px-[12px] py-[8px] lg:px-[18px] 2xl:px-[28px] 2xl:py-[12px] bg-secondary-rectangle-one`}
            outerStyle={{
              "--hover-bg-color": "#ffffff",
              "--hover-text-color": "#000000",
            }}
            onClick={() => {
              trackEvents("enquire_header_clicked");

              //navigate("/find-your-room/move-in");
              window.open(`/find-your-room/move-in`, "_blank");
            }}
          />

          {/*<div
            className={`mr-8 xxs:mr-9 px-[8px] xxs:px-[12px] w-[60px] xxs:w-[72px] h-[30px] rounded-full lg:hidden flex items-center justify-between border border-secondary-gray/30 ${
              isHeaderSticky ? "bg-secondary-gray/15" : "bg-pure-white/15"
            }`}
          >
            <a
              href="https://wa.me/+447454549548?text=Hi there! I'm interested in learning more about the accommodation options you offer."
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvents("whatsapp_mobile_header_clicked")}
            >
              <WhatsAppIcon
                className={`w-[12px] h-[12px] xxs:w-[16px] xxs:h-[16px] ${
                  isHeaderSticky ? "text-whatsapp-green" : "text-pure-white"
                }`}
              />
            </a>
            <p className="text-secondary-gray/30">|</p>
            <a
              href="tel:+447454549548"
              onClick={() => trackEvents("phone_mobile_header_clicked")}
            >
              <PhoneSolidIcon
                className={`w-[12px] h-[12px] xxs:w-[14px] xxs:h-[14px] ${
                  isHeaderSticky ? "text-primary-black" : "text-pure-white"
                }`}
              />
            </a>
          </div>*/}

          <div className="flex items-center gap-[14px] relative">
            <>
              <button
                ref={languageToggleRef}
                className="hidden xl:flex items-center gap-[8px]"
                onClick={toggleLanguageMenu}
              >
                {/* <img
                  src={selectedLanguage.flag}
                  alt={`${selectedLanguage.label} Flag`}
                  className="w-[24px] h-[24px] rounded-full shrink-0"
                /> */}
                {FlagIcon && (
                  <FlagIcon className="w-[24px] h-[24px] shrink-0" />
                )}
                <div className="flex items-center gap-[4px]">
                  <span
                    className={`font-bold text-[14px] ${
                      isHeaderSticky ? "text-primary-black" : "text-pure-white"
                    }`}
                  >
                    {selectedLanguage.value.toUpperCase()}
                  </span>
                  <ChevronDownIcon
                    className={`w-[10px] h-[10px] shrink-0 transition-transform duration-200 ${
                      isLanguageMenuOpen ? "rotate-180" : ""
                    } ${
                      isHeaderSticky ? "text-primary-black" : "text-pure-white"
                    }`}
                  />
                </div>
              </button>
              {isLanguageMenuOpen && (
                <div
                  ref={languageMenuRef}
                  className="absolute z-[100] top-12 left-0"
                >
                  <LanguageMenu
                    options={languageOptions}
                    selectedOption={selectedLanguage}
                    onSelect={handleLanguageSelect}
                  />
                </div>
              )}
            </>

            <div
              className={`hidden xl:inline-flex w-[1px] h-[15px] ${
                isHeaderSticky ? "bg-primary-black" : "bg-pure-white"
              }`}
            ></div>
            <button
              onClick={() => window.open(`/favourite`, "_blank")}
              // onClick={() => navigate("/favourite")}
              className="hidden xl:inline-flex group"
            >
              <HeartIcon
                className={`w-[24px] h-[24px] shrink-0 ${
                  isHeaderSticky
                    ? "text-primary-black fill-transparent hover:fill-secondary-rectangle-one"
                    : "text-pure-white fill-transparent hover:fill-secondary-rectangle-one"
                }`}
              />
            </button>

            <div
              className={`hidden xl:inline-flex w-[1px] h-[15px] ${
                isHeaderSticky ? "bg-primary-black" : "bg-pure-white"
              }`}
            ></div>

            <div
              onClick={() => {
                trackEvents("login_header_clicked");

                //navigate("/redirect-to-login");
                window.open(`/redirect-to-login`, "_blank");
              }}
              className="cursor-pointer hidden 2xl:inline-flex items-center gap-[4px]"
            >
              <UserIcon
                className={`w-[24px] h-[24px] shrink-0 ${
                  isHeaderSticky ? "text-primary-black" : "text-pure-white"
                }`}
              />
              <p
                className={`font-bold text-[14px] ${
                  isHeaderSticky ? "text-primary-black" : "text-pure-white"
                }`}
              >
                Login
              </p>
            </div>

            <button
              onClick={toggleMenu}
              className="group flex justify-center items-center h-[20px] w-[22px] lg:w-[42px] lg:h-[42px] shrink-0 relative bg-transparent rounded-[8px] lg:hover:bg-secondary-rectangle-one lg:sheen"
            >
              <HamburgerIcon
                className={`relative z-10 h-[18px] w-[18px] xxs:w-[22px] xxs:h-[22px] lg:w-[26px] lg:h-[26px] shrink-0 ${
                  isHeaderSticky
                    ? "text-primary-black lg:group-hover:text-pure-white"
                    : "text-pure-white"
                } transition-colors duration-200`}
              />
            </button>
          </div>
        </div>
      </div>
      {isLocationsOpen && (
        <>
          <div
            className="fixed inset-0 bg-pure-black/50 z-[99]"
            onClick={toggleLocations}
          />
          <div
            className="fixed left-0 right-0 z-[100] p-[16px]"
            style={{
              top: `${
                headerHeight + (showLevel && !isHeaderSticky ? 40 : 0)
              }px`,
            }}
          >
            <LocationsPopperMenu
              cityData={cityData}
              setIsLocationsOpen={setIsLocationsOpen}
            />
          </div>
        </>
      )}

      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={toggleMenu}
        logo={LogoWhite}
        navigationOptions={data?.links}
        cityData={cityData}
        languageOptions={languageOptions}
        selectedLanguage={selectedLanguage}
        onLanguageSelect={handleLanguageSelect}
      />
    </>
  );
}
